import { useAppDispatch } from "@/hooks/useAppDispatch"
import { clearUser, setUser } from "@/store/slices/authSlice"
import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { Navigate } from "react-router-dom"
import { api } from "@/services/api"
import {
    isControlPlaneEnabled,
    ensureFreshL3Token,
    decodeL3TokenClaims,
    controlPlaneLogout,
    SCOPE_SUPERADMIN,
} from "@/services/controlPlane"


// Create authentication context
const AuthContext = createContext<{
    isAuthenticated: boolean
    isLoading: boolean
    checkAuth: () => Promise<void>
    logout: () => Promise<void>
}>({
    isAuthenticated: false,
    isLoading: true,
    checkAuth: async () => {},
    logout: async () => {},
})

export const useAuth = () => useContext(AuthContext)

// Auth Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const dispatch = useAppDispatch()

    // Control Plane path: authorization comes from the signed superadmin scope
    // on the CP-minted L3 token; /auth/user (bearer) supplies display fields
    // only — the server authorizes on the scope, never the projected role.
    const checkControlPlaneAuth = async () => {
        const token = await ensureFreshL3Token()
        const claims = token ? decodeL3TokenClaims(token) : null
        if (!claims?.scopes.includes(SCOPE_SUPERADMIN)) {
            setIsAuthenticated(false)
            return
        }
        const response = await api.get('/auth/user')
        const profile = response.data.user ?? response.data
        const user = { ...profile, isSuperadmin: true }
        localStorage.setItem('user', JSON.stringify(user))
        dispatch(setUser(user))
        setIsAuthenticated(true)
    }

    const checkAuth = async () => {
        try {
            if (isControlPlaneEnabled()) {
                await checkControlPlaneAuth()
                return
            }
            const response = await api.get('/auth/user')
            if (response.data.user) {
                localStorage.setItem('user', JSON.stringify(response.data.user))
                dispatch(setUser(response.data.user))
                setIsAuthenticated(true)
            } else {
                setIsAuthenticated(false)
            }
        } catch (error) {
            console.error('Failed to fetch user', error)
            setIsAuthenticated(false)
        } finally {
            setIsLoading(false)
        }
    }

    const logout = async () => {
        try {
            if (isControlPlaneEnabled()) {
                await controlPlaneLogout()
            } else {
                await api.get('/auth/logout')
            }
            localStorage.removeItem('user')
            dispatch(clearUser())
            setIsAuthenticated(false)
        } catch (error) {
            console.error('Logout failed', error)
        }
    }

    useEffect(() => {
        checkAuth()
    }, [])

    return (
        <AuthContext.Provider value={{ isAuthenticated, isLoading, checkAuth, logout }}>
            {children}
        </AuthContext.Provider>
    )
}

// Protected Route component. Superadmin is the signed scope on the CP token,
// stamped onto the stored user as `isSuperadmin` by checkControlPlaneAuth; the
// projected `role` is an org role and never gates the panel.
export function ProtectedRoute({ children, requireSuperadmin = false }: { children: ReactNode, requireSuperadmin?: boolean }) {
    const { isAuthenticated, isLoading } = useAuth()
    const storedUserRaw = typeof window !== 'undefined' ? localStorage.getItem('user') : null
    const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) as { isSuperadmin?: boolean } : null

    if (isLoading) {
        // Return a loading state or null to prevent flash
        return null
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" />
    }

    if (requireSuperadmin && storedUser?.isSuperadmin !== true) {
        return <Navigate to="/login" />
    }

    return <>{children}</>
}
